import React, { useMemo } from 'react';
import { Alert, Box, Button, Checkbox, FormControlLabel, MenuItem, Stack, TextField, Typography } from '@mui/material';
import { MaterialReactTable } from 'material-react-table';
import { useMRTState } from '../../../../hooks/useMRTState';
import { getMRTDefaultProps } from '../../../../helpers/mrt/getMRTDefaultProps';

export default function DocumentosTab({ modulo: m, eleicaoUuid, permissoes: p, parametro, isMobile }) {
    const mrt = useMRTState({ storageKey: 'Eleicao.Documentos', pageSizeDefault: Number(parametro?.paginacao) || 5 });
    const defaults = getMRTDefaultProps({ mrt, parametro, isMobile });
    const columns = useMemo(
        () => [
            {
                id: 'documentos',
                header: 'Cadastro da documentos',
                columns: [
                    { accessorKey: 'titulo', header: 'Documento', filterFn: 'contains' },
                    {
                        accessorKey: 'dataDocumento',
                        header: 'Data',
                        filterFn: 'contains',
                        Cell: ({ cell }) => cell.getValue()?.split('-').reverse().join('/') || '',
                    },
                    {
                        id: 'emitido',
                        header: 'PDF emitido',
                        accessorFn: (row) => (row.arquivoGerado ? 'Sim' : 'Não'),
                        filterFn: 'contains',
                    },
                    {
                        id: 'anexo',
                        header: 'PDF anexado',
                        accessorFn: (row) => (row.arquivoAssinado ? 'Sim' : 'Não'),
                        filterFn: 'contains',
                    },
                ],
            },
        ],
        []
    );
    if (!eleicaoUuid)
        return (
            <Alert severity="info" sx={{ my: 2 }}>
                Salve a eleição e abra novamente para cadastrar documentos.
            </Alert>
        );
    const fechado = Boolean(m.item.arquivoGerado || m.item.arquivoAssinado);
    const podeAlterar = m.item.uuid ? p.editar : p.incluir;
    const disabled = m.ocupado || fechado || !podeAlterar;
    const selecionar = (row) => m.selecionar(row.original);
    return (
        <Stack spacing={2} sx={{ py: 2 }}>
            <Alert severity="info">
                Documentos são salvos nesta aba. O modelo usa os dados já gravados da eleição; salve alterações do cabeçalho e da
                comissão antes de emitir.
            </Alert>
            {m.erro && (
                <Alert
                    severity="error"
                    action={
                        <Button type="button" onClick={m.carregar}>
                            Recarregar
                        </Button>
                    }
                >
                    {m.erro}
                </Alert>
            )}
            <MaterialReactTable
                {...defaults}
                columns={columns}
                data={m.lista}
                getRowId={(r) => r.uuid}
                manualFiltering={false}
                manualSorting={false}
                manualPagination={false}
                rowCount={undefined}
                enableRowSelection={false}
                autoResetPageIndex
                renderRowActions={
                    isMobile
                        ? undefined
                        : ({ row }) => (
                              <Button type="button" disabled={m.ocupado || m.pendente} onClick={() => selecionar(row)}>
                                  Selecionar
                              </Button>
                          )
                }
                renderRowActionMenuItems={
                    isMobile
                        ? ({ row, closeMenu }) => [
                              <MenuItem
                                  key="selecionar"
                                  disabled={m.ocupado || m.pendente}
                                  onClick={() => {
                                      closeMenu();
                                      selecionar(row);
                                  }}
                              >
                                  Selecionar
                              </MenuItem>,
                          ]
                        : undefined
                }
            />
            <Stack direction="row" spacing={1} useFlexGap flexWrap="wrap">
                <Button type="button" disabled={m.ocupado} onClick={m.novo}>
                    {m.pendente ? 'Descartar edição' : 'Limpar seleção'}
                </Button>
                <Button type="button" disabled={m.ocupado || m.pendente || !p.incluir} onClick={m.modelo}>
                    Usar modelo de diretrizes
                </Button>
                <Button type="button" disabled={m.ocupado || !m.item.uuid || !p.incluir} onClick={m.copiar}>
                    Criar cópia
                </Button>
            </Stack>
            {fechado && (
                <Alert severity="info">Conteúdo preservado após emissão ou anexação. Para alterar o texto, crie uma cópia.</Alert>
            )}
            <TextField
                name="titulo"
                label="Título"
                value={m.item.titulo}
                onChange={m.alterar}
                disabled={disabled}
                size="small"
                inputProps={{ maxLength: 255 }}
            />
            <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', sm: '1fr 2fr' }, gap: 2 }}>
                <TextField
                    name="dataDocumento"
                    label="Data do documento"
                    type="date"
                    value={m.item.dataDocumento || ''}
                    onChange={m.alterar}
                    disabled={disabled}
                    InputLabelProps={{ shrink: true }}
                    size="small"
                />
                <TextField
                    name="localEmissao"
                    label="Local de emissão (cidade/UF)"
                    value={m.item.localEmissao || ''}
                    onChange={m.alterar}
                    disabled={disabled}
                    size="small"
                    inputProps={{ maxLength: 255 }}
                />
            </Box>
            <TextField
                name="texto"
                label="Texto do documento"
                value={m.item.texto || ''}
                onChange={m.alterar}
                disabled={disabled}
                multiline
                minRows={10}
                helperText="Revise o modelo e substitua os trechos entre colchetes. Para anexar um documento externo, o texto pode ficar vazio."
                inputProps={{ maxLength: 100000 }}
            />
            <FormControlLabel
                control={
                    <Checkbox
                        name="incluirComissao"
                        checked={!!m.item.incluirComissao}
                        onChange={m.alterar}
                        disabled={disabled}
                    />
                }
                label="Incluir nomes e cargos da comissão para assinatura"
            />
            <Stack direction="row" spacing={1} useFlexGap flexWrap="wrap">
                <Button type="button" variant="contained" disabled={disabled || !m.sujo} onClick={m.salvar}>
                    Salvar documento
                </Button>
                <Button
                    type="button"
                    disabled={m.ocupado || m.pendente || !m.item.uuid || fechado || !p.imprimir}
                    onClick={m.visualizar}
                >
                    Visualizar prévia
                </Button>
                <Button
                    type="button"
                    disabled={m.ocupado || m.pendente || !m.item.uuid || fechado || !p.editar || !p.imprimir}
                    onClick={m.emitir}
                >
                    Emitir PDF
                </Button>
                <Button
                    type="button"
                    disabled={m.ocupado || !m.item.arquivoGerado || !p.imprimir}
                    onClick={() => m.abrir(m.item, 'emitido')}
                >
                    Abrir emitido
                </Button>
                <Button type="button" component="label" disabled={m.ocupado || m.sujo || !m.item.uuid || !p.editar}>
                    Selecionar PDF assinado / externo
                    <input hidden type="file" accept="application/pdf,.pdf" onChange={m.escolherArquivo} />
                </Button>
                <Button type="button" disabled={m.ocupado || m.sujo || !m.arquivo || !p.editar} onClick={m.anexar}>
                    Enviar PDF
                </Button>
                <Button
                    type="button"
                    disabled={m.ocupado || !m.item.arquivoAssinado || !p.imprimir}
                    onClick={() => m.abrir(m.item, 'assinado')}
                >
                    Abrir anexo
                </Button>
                <Button
                    type="button"
                    color="error"
                    disabled={m.ocupado || m.pendente || !m.item.arquivoAssinado || !p.cancelar}
                    onClick={() => m.remover(true)}
                >
                    Remover anexo
                </Button>
                <Button
                    type="button"
                    color="error"
                    disabled={m.ocupado || m.pendente || !m.item.uuid || !p.cancelar}
                    onClick={() => m.remover(false)}
                >
                    Excluir documento
                </Button>
            </Stack>
            {(m.arquivo || m.item.nomeOriginal) && (
                <Typography variant="body2">
                    {m.arquivo ? `Selecionado para envio: ${m.arquivo.name}` : `Anexado: ${m.item.nomeOriginal}`}
                </Typography>
            )}
        </Stack>
    );
}
